import { z } from 'zod';
import { ClaudeCodeInvocationNameSchema } from '@terra/shared-schemas-claude-code';
import {
  EvaluationInferenceProviderIdSchema,
  EvaluationPlaybookPhaseSchema,
} from './evaluation-run-shared.schemas';

export const EvaluationComponentDataRequirementSchema = z.object({
  kind: z.string(),
  required: z.boolean(),
  description: z.string(),
  constraints: z.record(z.string(), z.unknown()).optional(),
});

export const EvaluationComponentScenarioInputFieldOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
});

export const EvaluationComponentScenarioInputFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  kind: z.enum(['enum', 'text']),
  optional: z.boolean().optional(),
  description: z.string().optional(),
  options: z
    .array(EvaluationComponentScenarioInputFieldOptionSchema)
    .optional(),
});

export const EvaluationOutcomeMatcherSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('exact') }),
  z.object({ kind: z.literal('case-insensitive') }),
  z.object({ kind: z.literal('contains') }),
  z.object({ kind: z.literal('numeric-tolerance'), tolerance: z.number() }),
  z.object({
    kind: z.literal('set-member'),
    separator: z.string().optional(),
  }),
  z.object({
    kind: z.literal('equivalence-classes'),
    classes: z.array(z.array(z.string())),
  }),
]);

export const EvaluationOutcomeDescriptorSchema = z.object({
  id: z.string(),
  label: z.string(),
  kind: z.enum(['enum', 'text', 'boolean']),
  evaluatorId: z.string(),
  matcher: EvaluationOutcomeMatcherSchema.optional(),
  expected: z.object({
    source: z.literal('scenario-input'),
    key: z.string(),
  }),
  actual: z.array(
    z.object({
      source: z.enum(['trace-metadata', 'run-metadata']),
      path: z.array(z.string()),
    }),
  ),
  description: z.string().optional(),
  enumOptions: z
    .array(EvaluationComponentScenarioInputFieldOptionSchema)
    .optional(),
});

export const EvaluationSlotCapabilitySchema = z.enum([
  'model',
  'temperature',
  'systemPrompt',
  'tools',
]);

export const EvaluationComponentOverridableSlotSchema = z.preprocess(
  (value) => {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return value;
    }

    const record = value as Record<string, unknown>;

    if (Array.isArray(record.capabilities)) {
      return value;
    }

    if (record.supports === 'full') {
      return {
        ...record,
        capabilities: ['model', 'temperature', 'systemPrompt', 'tools'],
      };
    }

    if (record.supports === 'model-only') {
      return {
        ...record,
        capabilities: ['model', 'temperature'],
      };
    }

    return value;
  },
  z.object({
    slot: z.string(),
    label: z.string(),
    capabilities: z.array(EvaluationSlotCapabilitySchema),
    defaultModel: z.string().optional(),
    defaultTemperature: z.number().min(0).max(2).optional(),
    defaultSystemPrompt: z.string().optional(),
    defaultTools: z.array(z.string()).optional(),
    promptSource: z.enum(['static', 'runtime']).optional(),
    parentSlot: z.string().optional(),
    depth: z.int().nonnegative().optional(),
  }),
);

export const EvaluationComponentSchema = z.object({
  id: z.string(),
  kind: z.enum(['graph', 'subgraph', 'agent', 'llm-call']),
  engine: z.string(),
  label: z.string(),
  description: z.string(),
  dispatch: z.enum(['direct', 'event']),
  inputJsonSchema: z.record(z.string(), z.unknown()).nullable(),
  outputJsonSchema: z.record(z.string(), z.unknown()).nullable(),
  dataRequirements: z.array(EvaluationComponentDataRequirementSchema),
  evaluators: z.array(z.string()),
  outcomeDescriptors: z.array(EvaluationOutcomeDescriptorSchema).optional(),
  scenarioInputFields: z
    .array(EvaluationComponentScenarioInputFieldSchema)
    .optional(),
  overridableSlots: z
    .array(EvaluationComponentOverridableSlotSchema)
    .optional(),
  overridablePlaybookPhases: z.array(EvaluationPlaybookPhaseSchema).optional(),
  overridablePlaybookCategories: z
    .array(
      z.object({
        category: z.string().min(1),
        label: z.string().min(1),
      }),
    )
    .optional(),
  vectorType: z.string().optional(),
  invocation: ClaudeCodeInvocationNameSchema.optional(),
});

export const ListEvaluationComponentsResponseSchema = z.object({
  components: z.array(EvaluationComponentSchema),
  overrideModels: z.array(z.string()).optional(),
  overrideModelCatalog: z
    .array(
      z.object({
        id: z.string(),
        label: z.string(),
        vendor: z.enum([
          'anthropic',
          'fireworks',
          'google',
          'meta',
          'nvidia',
          'openai',
          'vertex',
          'xai',
        ]),
        deployedProviders: z
          .array(z.enum(['bedrock', 'fireworks', 'gateway', 'vertex']))
          .optional(),
        platformProvider: z.enum(['bedrock', 'fireworks', 'gateway', 'vertex']),
        reasoningEfforts: z
          .array(z.enum(['low', 'medium', 'high', 'xhigh', 'max']))
          .optional(),
        supportsTemperature: z.boolean(),
        temperature: z.number().min(0).max(2).optional(),
      }),
    )
    .optional(),
  overrideProviders: z
    .array(
      z.object({
        providerId: EvaluationInferenceProviderIdSchema,
        label: z.string(),
        backend: EvaluationInferenceProviderIdSchema,
        description: z.string(),
        icon: EvaluationInferenceProviderIdSchema,
        configured: z.boolean(),
        supportedModelVendors: z.array(
          z.enum([
            'anthropic',
            'fireworks',
            'google',
            'meta',
            'nvidia',
            'openai',
            'vertex',
            'xai',
          ]),
        ),
      }),
    )
    .optional(),
  availableTools: z.array(z.string()).optional(),
});

export const EvaluationComponentTrendsQuerySchema = z.object({
  componentId: z.string().min(1),
  windowDays: z.coerce.number().min(1).max(180).optional(),
});

export const EvaluationComponentTrendPointSchema = z.object({
  date: z.string(),
  runCount: z.number(),
  meanScore: z.number().nullable(),
  passRate: z.number().nullable(),
});

export const EvaluationComponentTrendsResponseSchema = z.object({
  componentId: z.string(),
  points: z.array(EvaluationComponentTrendPointSchema),
  baselineMeanScore: z.number().nullable(),
  latestMeanScore: z.number().nullable(),
  regression: z.boolean(),
});

export const InvokeEvaluationComponentParamsSchema = z.object({
  componentId: z.string().min(1),
});

export const InvokeEvaluationComponentBodySchema = z.object({
  input: z.record(z.string(), z.unknown()),
  evaluationRunId: z.string().min(1).optional(),
});

export const InvokeEvaluationComponentResponseSchema = z.object({
  status: z.enum(['COMPLETED', 'FAILED']),
  output: z.record(z.string(), z.unknown()),
  evaluators: z.array(z.string()),
  traceId: z.string().optional(),
  threadId: z.string().optional(),
  error: z.string().optional(),
});

export const GetEvaluationPostAttackPlaybookQuerySchema = z.object({
  signalCategory: z.string().min(1),
});

export const EvaluationPlaybookCategorySchema = z.object({
  category: z.string().min(1),
  label: z.string().min(1),
});

export const GetEvaluationPostAttackPlaybookResponseSchema = z.object({
  signalCategory: z.string(),
  content: z.string().nullable(),
  categories: z.array(EvaluationPlaybookCategorySchema),
});

export type EvaluationComponent = z.infer<typeof EvaluationComponentSchema>;
export type EvaluationComponentScenarioInputField = z.infer<
  typeof EvaluationComponentScenarioInputFieldSchema
>;
export type EvaluationComponentOverridableSlot = z.infer<
  typeof EvaluationComponentOverridableSlotSchema
>;
export type EvaluationOutcomeDescriptor = z.infer<
  typeof EvaluationOutcomeDescriptorSchema
>;
export type EvaluationOverrideModel = NonNullable<
  z.infer<typeof ListEvaluationComponentsResponseSchema>['overrideModelCatalog']
>[number];
export type EvaluationOverrideProvider = NonNullable<
  z.infer<typeof ListEvaluationComponentsResponseSchema>['overrideProviders']
>[number];
export type EvaluationComponentTrends = z.infer<
  typeof EvaluationComponentTrendsResponseSchema
>;
export type InvokeEvaluationComponentResponse = z.infer<
  typeof InvokeEvaluationComponentResponseSchema
>;
export type EvaluationPlaybookCategory = z.infer<
  typeof EvaluationPlaybookCategorySchema
>;
export type GetEvaluationPostAttackPlaybookResponse = z.infer<
  typeof GetEvaluationPostAttackPlaybookResponseSchema
>;
